import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Menu, X, Phone, Mail, Search } from "lucide-react";
import SearchBar from "./product/SearchBar";
import Constant from "../utils/Constant";
import LoadingSpinner from "./LoadingSpinner"

const Navbar = () => {
  
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showTagline, setShowTagline] = useState(false);
  const [contactData, setContactData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [link, setLink] = useState(null);
  
  // Track loading states for both API calls
  const [contactLoading, setContactLoading] = useState(true);
  const [linkLoading, setLinkLoading] = useState(true);
  
  useEffect(() => {
    setShowTagline(true);
  }, []);
  
  useEffect(() => {
    const fetchLink = async () => {
      try {
        setLinkLoading(true);
        const response1 = await fetch(`${Constant.BASE_URL}/api/v1/get-qlc-info`);
        if (!response1.ok) throw new Error(`API 1 failed: ${response1.status}`);
        const data1 = await response1.json();
        setLink(data1.data);
        console.log("Link", data1.data[0].qic_link);
      } catch (error) {
        console.error("Error fetching QIC link:", error);
        setLink([]);
      } finally {
        setLinkLoading(false);
      }
    };
    fetchLink();
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  useEffect(() => {
    fetchContactData();
  }, []);

  const fetchContactData = async () => {
    try {
      setContactLoading(true);
      const response = await fetch(`${Constant.BASE_URL}/api/v1/profile`);
      const result = await response.json();
      if (result.status && result.data?.length > 0) {
        setContactData(result.data[0]);
      }
    } catch (error) {
      console.error("Error fetching custom products data:", error);
    } finally {
      setContactLoading(false);
    }
  };

  // Update main loading state based on both API calls
  useEffect(() => {
    setLoading(contactLoading || linkLoading);
  }, [contactLoading, linkLoading]);

  const handleCatalogDownload = async () => {
    try {
      setCatalogLoading(true);
      const response = await fetch(`${Constant.BASE_URL}/api/v1/get-catalog`);
      const data = await response.json();
      
      if (data.status && data.data[0]?.catalog_pdf) {
        const pdfUrl = `${Constant.BASE_URL}/${data.data[0].catalog_pdf}`;
        window.open(pdfUrl, '_blank');
      }
    } catch (error) {
      console.error('Error downloading catalog:', error);
    } finally {
      setCatalogLoading(false);
    }
  };

  // Show loading screen until all data is loaded
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
       <LoadingSpinner/>
      </div>
    );
  }

  const renderContactInfo = () => {
    return (
      <div className="flex flex-col sm:flex-row justify-end space-y-1 sm:space-y-0 sm:space-x-4 text-xs sm:text-sm">
        {contactData?.profile_mobile_status === "active" && (
          <a
            href={`tel:+44${contactData?.profile_mobile}`}
            className="hover:text-gray-200 flex items-center justify-center sm:justify-start"
          >
            <Phone className="h-4 w-4 mr-1" />
            {contactData?.profile_mobile}
          </a>
        )}
        {contactData?.profile_email_status === "active" && (
          <a
            href={`mailto:${contactData?.profile_email}`}
            className="hover:text-gray-200 flex items-center justify-center sm:justify-start"
          >
            <Mail className="h-4 w-4 mr-1" />
            {contactData?.profile_email}
          </a>
        )}
      </div>
    );
  };

  return (
    <div className="w-full">
      {/* Top Contact Bar */}
      <div
        className="w-full text-white py-2"
        style={{
          background: "linear-gradient(to right, rgba(125, 207, 182, 1), rgba(9, 156, 214, 1))",
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {renderContactInfo()}
        </div>
      </div>

      {/* Main Navigation */}
      <div className="w-full bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-20">
            
            {/* LEFT SECTION - Logo */}
            <div className="flex-shrink-0 w-auto lg:w-1/4">
              <Link to="/" className="flex items-center">
                <div className="flex flex-col font-serif">
                  <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#099CD6] font-['Times_New_Roman',_serif]">
                    Ziegler
                  </h1>
                  <div className="overflow-hidden">
                    <p
                      className={`text-sm lg:text-sm text-[#848bb6] font-['Futura',_sans-serif] font-semibold transform transition-transform duration-1000 ${
                        showTagline ? "translate-x-0" : "-translate-x-full"
                      }`}
                    >
                      Redefine Innovative Metering
                    </p>
                  </div>
                </div>
              </Link>
            </div>

            {/* MIDDLE SECTION - Navigation Menu */}
            <div className="hidden lg:flex flex-1 justify-center px-8">
              <nav className="flex items-center space-x-8">
                <Link
                  to="/aboutus"
                  className="text-gray-600 font-semibold transition-all duration-300 hover:bg-gradient-to-r hover:from-[rgba(125,207,182,1)] hover:to-[rgba(9,156,214,1)] hover:text-transparent hover:bg-clip-text whitespace-nowrap"
                >
                  About Us
                </Link>
                <Link
                  to="/product"
                  className="text-gray-600 font-semibold transition-all duration-300 hover:bg-gradient-to-r hover:from-[rgba(125,207,182,1)] hover:to-[rgba(9,156,214,1)] hover:text-transparent hover:bg-clip-text whitespace-nowrap"
                >
                  Products
                </Link>
                {link && link.length > 0 ? (
                  <Link 
                    to={link[0].qic_link}
                    target="_blank"
                    className="text-gray-600 font-semibold cursor-pointer transition-all duration-300 hover:bg-gradient-to-r hover:from-[rgba(125,207,182,1)] hover:to-[rgba(9,156,214,1)] hover:text-transparent hover:bg-clip-text whitespace-nowrap"
                    aria-label="QIC Info"
                  >
                    QIC
                  </Link>
                ) : (
                  <span className="text-gray-600 font-semibold transition-all duration-300 whitespace-nowrap">
                    QIC
                  </span>
                )}
                <Link
                  to="/contactus"
                  className="text-gray-600 font-semibold transition-all duration-300 hover:bg-gradient-to-r hover:from-[rgba(125,207,182,1)] hover:to-[rgba(9,156,214,1)] hover:text-transparent hover:bg-clip-text whitespace-nowrap"
                >
                  Contact Us
                </Link>
              </nav>
            </div>

            {/* RIGHT SECTION - Actions */}
            <div className="hidden lg:flex items-center space-x-4 w-auto lg:w-1/4 justify-end">
              <button 
                className={`flex items-center text-sm px-4 py-2 rounded-full bg-gradient-to-r from-[rgba(125,207,182,1)] to-[rgba(9,156,214,1)] text-white hover:shadow-lg transition-all duration-300 whitespace-nowrap ${
                  catalogLoading ? 'opacity-75 cursor-not-allowed' : ''
                }`}
                onClick={handleCatalogDownload}
                disabled={catalogLoading}
              >
                {catalogLoading ? 'Loading...' : 'Download Catalog'}
                <svg
                  className={`w-4 h-4 ml-2 transition-transform duration-300 ${
                    catalogLoading ? '' : 'group-hover:-translate-y-1'
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                  />
                </svg>
              </button>
              
       <div className="min-w-[35px] sm:min-w-[85px] md:min-w-[135px] lg:min-w-[185px] xl:min-w-[235px]">
  <SearchBar />
</div>





            </div>

            {/* Mobile Menu Button */}
            <div className="lg:hidden">
              <button
                className="text-gray-600 hover:text-gray-900 p-2"
                onClick={toggleMenu}
                aria-label="Toggle menu"
              >
                {isMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          <div className={`lg:hidden ${isMenuOpen ? "block" : "hidden"} pb-4`}>
            <div className="space-y-4">
              {/* Mobile Navigation */}
              <nav className="flex flex-col space-y-4">
                <Link
                  to="/aboutus"
                  className="text-gray-600 font-semibold text-center py-2 transition-all duration-300 hover:bg-gradient-to-r hover:from-[rgba(125,207,182,1)] hover:to-[rgba(9,156,214,1)] hover:text-transparent hover:bg-clip-text"
                  onClick={() => setIsMenuOpen(false)}
                >
                  About Us
                </Link>
                <Link
                  to="/product"
                  className="text-gray-600 font-semibold text-center py-2 transition-all duration-300 hover:bg-gradient-to-r hover:from-[rgba(125,207,182,1)] hover:to-[rgba(9,156,214,1)] hover:text-transparent hover:bg-clip-text"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Products
                </Link>
                {link && link.length > 0 ? (
                  <Link 
                    to={link[0].qic_link}
                    target="_blank"
                    className="text-gray-600 font-semibold text-center py-2 cursor-pointer transition-all duration-300 hover:bg-gradient-to-r hover:from-[rgba(125,207,182,1)] hover:to-[rgba(9,156,214,1)] hover:text-transparent hover:bg-clip-text"
                    aria-label="QIC Info"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    QIC
                  </Link>
                ) : (
                  <span className="text-gray-600 font-semibold text-center py-2 transition-all duration-300">
                    QIC
                  </span>
                )}
                <Link
                  to="/contactus"
                  className="text-gray-600 font-semibold text-center py-2 transition-all duration-300 hover:bg-gradient-to-r hover:from-[rgba(125,207,182,1)] hover:to-[rgba(9,156,214,1)] hover:text-transparent hover:bg-clip-text"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Contact Us
                </Link>
              </nav>
              
              {/* Mobile Search */}
              <div className="w-full px-2">
                <SearchBar />
              </div>
              
              {/* Mobile Catalog Button */}
              <button 
                className={`w-full flex items-center justify-center text-sm px-4 py-2 rounded-full bg-gradient-to-r from-[rgba(125,207,182,1)] to-[rgba(9,156,214,1)] text-white hover:shadow-lg transition-all duration-300 ${
                  catalogLoading ? 'opacity-75 cursor-not-allowed' : ''
                }`}
                onClick={handleCatalogDownload}
                disabled={catalogLoading}
              >
                {catalogLoading ? 'Loading...' : 'Download Catalog'}
                <svg
                  className={`w-4 h-4 ml-2 transition-transform duration-300 ${
                    catalogLoading ? '' : 'group-hover:-translate-y-1'
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Navbar;